/**
 * WP SEO Health API service — Yoast SEO + Schema Pro data from WordPress.
 */
import { apiClient } from './apiClient';
import { useTenantStore } from '@/stores/useTenantStore';

const getTenantId = () => useTenantStore.getState().currentTenant;

/**
 * Some endpoints signal "not configured / upstream failure" with a 200 +
 * `{ error: string }` body. Treat that as a failure so React Query surfaces
 * isError instead of the page rendering it as "no data".
 */
function throwIfErrorBody(data: unknown): void {
  if (
    data !== null &&
    typeof data === 'object' &&
    typeof (data as { error?: unknown }).error === 'string'
  ) {
    throw new Error((data as { error: string }).error);
  }
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SeoPageEntry {
  id: number;
  title: string;
  url: string;
  post_type: string;
  post_modified: string;
  seo_score: number;
  readability_score: number;
  focus_keyword: string | null;
  meta_title: string | null;
  meta_description: string | null;
  is_cornerstone: boolean;
  robots_noindex: boolean;
  schema_page_type: string | null;
  schema_article_type: string | null;
  schema_pro_schemas: string[];
}

export interface SchemaTypeSummary {
  type: string;
  count: number;
}

export interface SeoAggregate {
  total_pages: number;
  avg_seo_score: number;
  avg_readability_score: number;
  pages_with_good_seo: number;
  pages_with_ok_seo: number;
  pages_with_poor_seo: number;
  pages_with_schema: number;
  pages_without_schema: number;
  pages_missing_meta_desc: number;
  cornerstone_count: number;
  noindex_count: number;
  schema_types_summary: SchemaTypeSummary[];
}

export interface PluginStatus {
  yoast_active: boolean;
  schema_pro_active: boolean;
}

export interface WPSeoHealthResponse {
  aggregate: SeoAggregate;
  pages: SeoPageEntry[];
  plugin_status: PluginStatus;
}

export interface WPSeoStatusResponse {
  tenant_id: string;
  plugin_status: PluginStatus;
  total_pages: number;
}

// ---------------------------------------------------------------------------
// API Functions
// ---------------------------------------------------------------------------

export async function fetchWPSeoHealth(
  tenantId: string | null | undefined,
): Promise<WPSeoHealthResponse> {
  const { data } = await apiClient.get<WPSeoHealthResponse>(
    '/wp-seo-health/data',
    {
      params: { tenant_id: tenantId ?? getTenantId() },
    },
  );
  throwIfErrorBody(data);
  return data;
}

export async function fetchWPSeoStatus(
  tenantId: string | null | undefined,
): Promise<WPSeoStatusResponse> {
  const { data } = await apiClient.get<WPSeoStatusResponse>(
    '/wp-seo-health/status',
    {
      params: { tenant_id: tenantId ?? getTenantId() },
    },
  );
  return data;
}
